import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { OpsApiError, getOpsApiBaseUrl, opsApiRequest } from '../api/client.ts';
import { BrandLogo } from '../components/BrandLogo.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { Button, FormField, Input, StatusLamp, buttonClass } from '../ui/index.ts';

/**
 * Operator login — the front door of the dispatch desk (epic #835 PR2).
 * Calls `POST /v1/auth/login` with the email + password pair and relies
 * on the browser to persist the returned session cookie.
 *
 * Visual contract: Dispatch Deck DESIGN.md — a raised card on the
 * canvas with the brand block and a lamp row (the only decorative
 * element; the ok lamp is lit, the rest idle). Visible `label`-tier
 * field labels via FormField; signal primary button.
 *
 * On success:
 *   - Populates session context with the user shape ops-api returned
 *     (ops_user_id, email, roles).
 *   - Navigates back to `location.state.from` (the path the guard
 *     bounced from) if present, else `/`.
 *
 * On failure: surfaces the ops-api error code + message inline AND
 * via a toast so the operator notices even after switching tabs.
 */

interface LoginResponse {
  ops_user_id: string;
  email: string;
  display_name?: string;
  roles: readonly string[];
}

interface LocationStateFrom {
  from?: string;
}

export function Login(): JSX.Element {
  const { status, setUser, fetchImpl } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rawState: unknown = location.state;
  const returnTo =
    rawState !== null && typeof rawState === 'object'
      ? (rawState as LocationStateFrom).from
      : undefined;
  const safeReturnTo = typeof returnTo === 'string' && returnTo.length > 0 ? returnTo : '/';
  const feishuStartUrl = `${getOpsApiBaseUrl()}/v1/auth/feishu/start?return_to=${encodeURIComponent(
    safeReturnTo,
  )}`;
  const feishuError = new URLSearchParams(location.search).get('feishu_error');
  const feishuErrorMessage = feishuError === null ? null : feishuErrorToMessage(feishuError, t);
  const displayError = error ?? feishuErrorMessage;

  // If someone visits /login while already authenticated, drop them
  // back into the shell. This avoids a flash of the form on refresh.
  if (status === 'authenticated') {
    // Honor the guard bounce here too: react-router 7 runs navigate() inside
    // startTransition, so the post-login re-render hits this guard before the
    // explicit navigate(returnTo) lands.
    return <Navigate to={safeReturnTo} replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (email.trim().length === 0 || password.length === 0) {
      setError(t('login.error.empty_fields'));
      return;
    }
    setSubmitting(true);
    try {
      const response = await opsApiRequest<LoginResponse>(
        '/v1/auth/login',
        {
          method: 'POST',
          body: { email: email.trim(), password },
        },
        fetchImpl,
      );
      setUser({
        opsUserId: response.ops_user_id,
        email: response.email,
        displayName:
          typeof response.display_name === 'string' && response.display_name.trim().length > 0
            ? response.display_name.trim()
            : response.email,
        roles: response.roles,
        // This request WAS a password login, so the account provably has
        // one. Login deliberately skips the /me round-trip, so the flag has
        // to come from the flow rather than from the server's answer.
        hasPassword: true,
      });
      navigate(safeReturnTo, {
        replace: true,
      });
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
      <div className="w-full max-w-[400px] rounded-xl border border-line bg-card p-8 text-paper shadow-raised">
        <div className="mb-7 flex items-center gap-3">
          <BrandLogo className="h-10 w-10 shrink-0 text-paper" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-title text-paper">{t('login.brand')}</span>
            <span className="font-mono text-micro text-paper-low">{t('login.subtitle')}</span>
          </div>
          <span className="ml-auto inline-flex items-center gap-1" aria-hidden="true">
            <StatusLamp tone="ok" size="sm" />
            <StatusLamp tone="off" size="sm" />
            <StatusLamp tone="off" size="sm" />
          </span>
        </div>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <FormField label={t('login.email')} fieldId="login-email">
            <Input
              id="login-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              disabled={submitting}
              required
              data-testid="login-email"
            />
          </FormField>
          <FormField label={t('login.password')} fieldId="login-password">
            <Input
              id="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              disabled={submitting}
              required
              data-testid="login-password"
            />
          </FormField>
          {displayError !== null ? (
            <p className="m-0 text-label text-bad" role="alert" data-testid="login-error">
              {displayError}
            </p>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            data-testid="login-submit"
            className="mt-1 h-9 w-full"
          >
            {submitting ? t('login.submitting') : t('login.submit')}
          </Button>
          <a href={feishuStartUrl} data-testid="login-feishu" className={`${buttonClass('secondary')} h-9 w-full`}>
            {t('login.feishu')}
          </a>
        </form>
      </div>
    </div>
  );
}

function feishuErrorToMessage(code: string, t: (key: I18nKey) => string): string {
  switch (code) {
    case 'not_linked':
      return t('login.feishu_error.not_linked');
    case 'disabled':
      return t('login.feishu_error.disabled');
    case 'state':
      return t('login.feishu_error.state');
    case 'tenant':
      return t('login.feishu_error.tenant');
    case 'exchange':
    case 'feishu':
    case 'platform':
    case 'config':
    default:
      return t('login.feishu_error.generic');
  }
}
